import { Text } from '@chatsdet/ui';
import {
  getNodeFields,
  pickCanonicalConfig,
  type NodeFieldSpec as FieldSpec,
} from '@chatsdet/workflow-diff';
import { CopyablePre } from '../../../components/CopyButton';

interface NodeConfigViewProps {
  config: Record<string, unknown>;
  /** Other side's config for this node id; used to mark changed fields. */
  compareTo?: Record<string, unknown>;
  nodeType: string;
}

export function NodeConfigView({
  config: rawConfig,
  compareTo: rawCompareTo,
  nodeType,
}: NodeConfigViewProps) {
  const fields = getNodeFields(nodeType);
  const config = pickCanonicalConfig(nodeType, rawConfig);
  const compareTo = rawCompareTo
    ? pickCanonicalConfig(nodeType, rawCompareTo)
    : undefined;

  // Only render fields that appear on at least one side, so pristine nodes
  // stay quiet even if their type supports many fields.
  const visible = fields.filter((f) => hasValue(config[f.key]) || (compareTo && hasValue(compareTo[f.key])));

  if (visible.length === 0) {
    return (
      <Text size="xs" className="italic text-muted-foreground">
        No configuration.
      </Text>
    );
  }

  const scalars = visible.filter((f) => !f.kind || f.kind === 'scalar');
  const complex = visible.filter((f) => f.kind && f.kind !== 'scalar');

  return (
    <div className="space-y-2">
      {scalars.length > 0 && (
        <div className="space-y-1">
          {scalars.map((f) => (
            <FieldRow
              key={f.key}
              spec={f}
              value={config[f.key]}
              other={compareTo?.[f.key]}
              hasOther={compareTo !== undefined && f.key in compareTo}
            />
          ))}
        </div>
      )}
      {complex.map((f) => (
        <ComplexBlock
          key={f.key}
          spec={f}
          value={config[f.key]}
          other={compareTo?.[f.key]}
          hasOther={compareTo !== undefined && f.key in compareTo}
        />
      ))}
    </div>
  );
}

function hasValue(v: unknown): boolean {
  if (v === undefined || v === null || v === '') return false;
  if (Array.isArray(v) && v.length === 0) return false;
  if (typeof v === 'object' && v !== null && !Array.isArray(v) && Object.keys(v).length === 0) return false;
  return true;
}

function valuesEqual(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  try { return JSON.stringify(a) === JSON.stringify(b); } catch { return false; }
}

// ── Scalar ───────────────────────────────────────────────────────────

type RowTone = 'neutral' | 'changed' | 'added' | 'removed';

function FieldRow({
  spec,
  value,
  other,
  hasOther,
}: {
  spec: FieldSpec;
  value: unknown;
  other: unknown;
  hasOther: boolean;
}) {
  const presentHere = hasValue(value);
  const presentOther = hasOther && hasValue(other);
  if (!presentHere && presentOther) {
    return <GhostField label={spec.label} />;
  }
  const isNew = presentHere && hasOther && !presentOther;
  const changed = presentHere && presentOther && !valuesEqual(value, other);
  const tone: RowTone = isNew ? 'added' : changed ? 'changed' : 'neutral';
  return (
    <RowBand
      tone={tone}
      label={spec.label}
      value={<ScalarValue fieldKey={spec.key} value={value} />}
    />
  );
}

function GhostField({ label }: { label: string }) {
  return (
    <RowBand
      tone="removed"
      label={label}
      value={<span className="text-sm italic text-muted-foreground">—</span>}
    />
  );
}

/**
 * Single-row band matching the section block treatment: same border + bg
 * tones, same rounding. Label and value sit inline with a consistent gap so
 * the row reads as one card rather than two cells.
 */
function RowBand({
  tone,
  label,
  value,
}: {
  tone: RowTone;
  label: string;
  value: React.ReactNode;
}) {
  const cls = rowToneClass(tone);
  return (
    <div
      className={`flex items-baseline gap-4 rounded-md px-3 py-1.5 ${cls}`}
    >
      <div className="min-w-[7.5rem] text-sm font-medium text-foreground/75">
        {label}
      </div>
      <div className="min-w-0 flex-1">{value}</div>
    </div>
  );
}

function rowToneClass(tone: RowTone): string {
  switch (tone) {
    case 'changed':
      return 'border border-amber-300 bg-amber-50/60 dark:border-amber-500/40 dark:bg-amber-950/20';
    case 'added':
      return 'border border-blue-300 bg-blue-50/60 dark:border-blue-500/40 dark:bg-blue-950/20';
    case 'removed':
      return 'border border-orange-300 bg-orange-50/60 dark:border-orange-500/40 dark:bg-orange-950/20';
    case 'neutral':
      return '';
  }
}

function ScalarValue({ fieldKey, value }: { fieldKey: string; value: unknown }) {
  if (!hasValue(value)) {
    return <span className="italic text-muted-foreground">—</span>;
  }
  if (typeof value === 'boolean') {
    return (
      <span
        className={[
          'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium',
          value
            ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300'
            : 'bg-muted text-muted-foreground',
        ].join(' ')}
      >
        {value ? 'On' : 'Off'}
      </span>
    );
  }
  if (typeof value === 'number') {
    return (
      <span className="font-mono text-sm">
        {fieldKey === 'delaySeconds' ? `${value}s` : String(value)}
      </span>
    );
  }
  if (typeof value === 'string') {
    const isCodeLike = /^\$var\.|^\[|^#|:has-text|data-/i.test(value);
    return (
      <span className={isCodeLike ? 'font-mono text-xs break-all' : 'text-sm break-words'}>
        {value}
      </span>
    );
  }
  return <span className="font-mono text-xs break-all">{JSON.stringify(value)}</span>;
}

// ── Complex ──────────────────────────────────────────────────────────

function ComplexBlock({
  spec,
  value,
  other,
  hasOther,
}: {
  spec: FieldSpec;
  value: unknown;
  other: unknown;
  hasOther: boolean;
}) {
  const presentHere = hasValue(value);
  const presentOther = hasOther && hasValue(other);
  if (!presentHere && presentOther) {
    return <GhostComplex label={spec.label} />;
  }
  const isNew = presentHere && hasOther && !presentOther;
  const changed = presentHere && presentOther && !valuesEqual(value, other);
  const tone = sectionTone({
    tone: isNew ? 'added' : changed ? 'changed' : 'neutral',
  });
  return (
    <section className={`rounded-md border px-3 py-2 ${tone}`}>
      <Text size="sm" weight="medium" className="mb-1.5 block text-foreground/75">
        {spec.label}
      </Text>
      <ComplexBody spec={spec} value={value} />
    </section>
  );
}

function GhostComplex({ label }: { label: string }) {
  const tone = sectionTone({ tone: 'removed' });
  return (
    <section className={`rounded-md border px-3 py-2 ${tone}`}>
      <Text size="sm" weight="medium" className="mb-1 block text-foreground/75">
        {label}
      </Text>
      <Text size="xs" className="italic text-muted-foreground">
        Removed on this side
      </Text>
    </section>
  );
}

function sectionTone({ tone }: { tone: 'added' | 'removed' | 'changed' | 'neutral' }): string {
  switch (tone) {
    case 'added':
      return 'border-blue-300 bg-blue-50/50 dark:border-blue-500/40 dark:bg-blue-950/20';
    case 'removed':
      return 'border-orange-300 bg-orange-50/50 dark:border-orange-500/40 dark:bg-orange-950/20';
    case 'changed':
      return 'border-amber-300 bg-amber-50/40 dark:border-amber-500/40 dark:bg-amber-950/15';
    case 'neutral':
      return 'border-border/60 bg-muted/30';
  }
}

function ComplexBody({ spec, value }: { spec: FieldSpec; value: unknown }) {
  if (spec.kind === 'kv' && isPlainObject(value)) {
    return <KeyValueBlock entries={value as Record<string, unknown>} />;
  }
  if (spec.kind === 'inline_assertions' && Array.isArray(value)) {
    return <InlineAssertions items={value as InlineAssertion[]} />;
  }
  if (spec.kind === 'json') {
    const text = JSON.stringify(value, null, 2);
    return (
      <CopyablePre value={text}>
        <pre className="max-w-full overflow-x-auto rounded bg-muted/40 p-2 text-[11px]">
          {text}
        </pre>
      </CopyablePre>
    );
  }
  return null;
}

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return (
    typeof v === 'object' &&
    v !== null &&
    !Array.isArray(v) &&
    Object.getPrototypeOf(v) === Object.prototype
  );
}

function KeyValueBlock({ entries }: { entries: Record<string, unknown> }) {
  const keys = Object.keys(entries);
  return (
    <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5 text-xs">
      {keys.map((k) => (
        <FragmentKV key={k} k={k} v={entries[k]} />
      ))}
    </dl>
  );
}

function FragmentKV({ k, v }: { k: string; v: unknown }) {
  return (
    <>
      <dt className="font-mono text-muted-foreground">{k}</dt>
      <dd className="min-w-0 break-all font-mono">{formatScalar(v)}</dd>
    </>
  );
}

interface InlineAssertion {
  description?: string;
  condition?: string;
  severity?: string;
  kind?: string;
}

function InlineAssertions({ items }: { items: InlineAssertion[] }) {
  return (
    <ul className="space-y-0.5">
      {items.map((it, i) => {
        const text = it.description ?? it.condition ?? '';
        const tag = it.severity ?? it.kind;
        return (
          <li key={i} className="flex items-start gap-2 text-xs">
            <span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-orange-500" />
            <span className="min-w-0 flex-1 break-words">{text}</span>
            {tag && (
              <span className="shrink-0 rounded bg-muted/70 px-1 py-0 font-mono text-[10px] text-foreground/80">
                {tag}
              </span>
            )}
          </li>
        );
      })}
    </ul>
  );
}

function formatScalar(v: unknown): string {
  if (v === null || v === undefined) return '—';
  if (typeof v === 'string') return v === '' ? '""' : v;
  if (typeof v === 'boolean' || typeof v === 'number') return String(v);
  try { return JSON.stringify(v); } catch { return String(v); }
}
